import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

const tmpColor = new THREE.Color();

/**
 * Collects many small static meshes and merges them into one draw call per
 * material. Every geometry gets a per-vertex colour so one material can carry
 * many tints.
 */
export class Batcher {
  private readonly buckets = new Map<string, { material: THREE.Material; geometries: THREE.BufferGeometry[] }>();
  private readonly matrix = new THREE.Matrix4();
  private readonly quaternion = new THREE.Quaternion();
  private readonly euler = new THREE.Euler();
  private readonly position = new THREE.Vector3();
  private readonly scale = new THREE.Vector3();

  constructor(private readonly materials: Record<string, THREE.Material>) {}

  add(
    key: string,
    geometry: THREE.BufferGeometry,
    color: THREE.ColorRepresentation,
    x: number,
    y: number,
    z: number,
    rotY = 0,
    sx = 1,
    sy = 1,
    sz = 1,
    rotX = 0,
    rotZ = 0,
  ): void {
    const material = this.materials[key];
    if (!material) throw new Error(`Unknown batch material: ${key}`);
    this.euler.set(rotX, rotY, rotZ, 'YXZ');
    this.quaternion.setFromEuler(this.euler);
    this.position.set(x, y, z);
    this.scale.set(sx, sy, sz);
    this.matrix.compose(this.position, this.quaternion, this.scale);
    this.addMatrix(key, geometry, color, this.matrix, material);
  }

  addMatrix(
    key: string,
    geometry: THREE.BufferGeometry,
    color: THREE.ColorRepresentation,
    matrix: THREE.Matrix4,
    material = this.materials[key],
  ): void {
    const geo = geometry.clone();
    geo.applyMatrix4(matrix);
    tmpColor.set(color);
    const count = geo.attributes.position.count;
    const colors = new Float32Array(count * 3);
    for (let i = 0; i < count; i += 1) {
      colors[i * 3] = tmpColor.r;
      colors[i * 3 + 1] = tmpColor.g;
      colors[i * 3 + 2] = tmpColor.b;
    }
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    let bucket = this.buckets.get(key);
    if (!bucket) {
      bucket = { material, geometries: [] };
      this.buckets.set(key, bucket);
    }
    bucket.geometries.push(geo);
  }

  build(castShadow: Set<string>, receiveShadow: Set<string>): THREE.Group {
    const group = new THREE.Group();
    for (const [key, bucket] of this.buckets) {
      if (bucket.geometries.length === 0) continue;
      const merged = mergeGeometries(bucket.geometries, false);
      for (const g of bucket.geometries) g.dispose();
      if (!merged) throw new Error(`Failed to merge batch ${key}`);
      merged.computeBoundingSphere();
      const mesh = new THREE.Mesh(merged, bucket.material);
      mesh.name = `batch:${key}`;
      mesh.castShadow = castShadow.has(key);
      mesh.receiveShadow = receiveShadow.has(key);
      mesh.matrixAutoUpdate = false;
      mesh.updateMatrix();
      group.add(mesh);
    }
    this.buckets.clear();
    return group;
  }
}
